/**
 * SoftLavka ERP — Unified Color Palette
 * Inspired by iOS 26 / macOS 26 Liquid Glass design language.
 *
 * This is the SINGLE SOURCE OF TRUTH for all colors in the application.
 * Login, Designer, and App mode all derive from this palette.
 *
 * Key principles:
 * - Translucent surfaces with depth (liquid glass)
 * - Subtle tints derived from primary blue
 * - High contrast text for readability
 * - Semantic colors consistent across all modes
 */

export const appColors = {
  // ── Primary — Liquid Blue ──
  primary: {
    main: '#007AFF',       // iOS system blue
    light: '#409CFF',
    dark: '#0062CC',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(0, 122, 255, 1)',
      light: 'rgba(64, 156, 255, 1)',
      dark: 'rgba(0, 98, 204, 1)',
      '0.1': 'rgba(0, 122, 255, 0.1)',
      '0.15': 'rgba(0, 122, 255, 0.15)',
      '0.2': 'rgba(0, 122, 255, 0.2)',
      '0.25': 'rgba(0, 122, 255, 0.25)',
      '0.3': 'rgba(0, 122, 255, 0.3)',
      '0.35': 'rgba(0, 122, 255, 0.35)',
      '0.4': 'rgba(0, 122, 255, 0.4)',
      '0.45': 'rgba(0, 122, 255, 0.45)',
      '0.5': 'rgba(0, 122, 255, 0.5)',
      '0.6': 'rgba(0, 122, 255, 0.6)',
      '0.7': 'rgba(0, 122, 255, 0.7)',
      '0.9': 'rgba(0, 122, 255, 0.9)',
    },
  },

  // ── Secondary — Indigo ──
  secondary: {
    main: '#5856D6',       // iOS system indigo
    light: '#7A79E0',
    dark: '#3634A3',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(88, 86, 214, 1)',
      light: 'rgba(122, 121, 224, 1)',
      dark: 'rgba(54, 52, 163, 1)',
      '0.1': 'rgba(88, 86, 214, 0.1)',
      '0.15': 'rgba(88, 86, 214, 0.15)',
      '0.2': 'rgba(88, 86, 214, 0.2)',
      '0.25': 'rgba(88, 86, 214, 0.25)',
      '0.3': 'rgba(88, 86, 214, 0.3)',
      '0.35': 'rgba(88, 86, 214, 0.35)',
      '0.4': 'rgba(88, 86, 214, 0.4)',
      '0.45': 'rgba(88, 86, 214, 0.45)',
      '0.5': 'rgba(88, 86, 214, 0.5)',
    },
  },

  // ── Semantic Colors (iOS 26 system) ──
  success: {
    main: '#34C759',       // iOS system green
    light: '#5DD27A',
    dark: '#248A3D',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(52, 199, 89, 1)',
      light: 'rgba(93, 210, 122, 1)',
      dark: 'rgba(36, 138, 61, 1)',
      '0.2': 'rgba(52, 199, 89, 0.2)',
      '0.3': 'rgba(52, 199, 89, 0.3)',
      '0.4': 'rgba(52, 199, 89, 0.4)',
    },
  },

  warning: {
    main: '#FF9500',       // iOS system orange
    light: '#FFAA33',
    dark: '#CC7700',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(255, 149, 0, 1)',
      light: 'rgba(255, 170, 51, 1)',
      dark: 'rgba(204, 119, 0, 1)',
    },
  },

  error: {
    main: '#FF3B30',       // iOS system red
    light: '#FF6259',
    dark: '#D70015',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(255, 59, 48, 1)',
      light: 'rgba(255, 98, 89, 1)',
      dark: 'rgba(215, 0, 21, 1)',
    },
  },

  info: {
    main: '#5AC8FA',       // iOS system teal / cyan
    light: '#7BD4FB',
    dark: '#32A4D8',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(90, 200, 250, 1)',
      light: 'rgba(123, 212, 251, 1)',
      dark: 'rgba(50, 164, 216, 1)',
      '0.2': 'rgba(90, 200, 250, 0.2)',
      '0.25': 'rgba(90, 200, 250, 0.25)',
      '0.3': 'rgba(90, 200, 250, 0.3)',
      '0.4': 'rgba(90, 200, 250, 0.4)',
    },
  },

  // ── AI Accent — Purple ──
  ai: {
    main: '#AF52DE',       // iOS system purple
    light: '#C77DEB',
    dark: '#8944AB',
    contrastText: '#FFFFFF',
    rgba: {
      main: 'rgba(175, 82, 222, 1)',
      light: 'rgba(199, 125, 235, 1)',
      dark: 'rgba(137, 68, 171, 1)',
      '0.15': 'rgba(175, 82, 222, 0.15)',
      '0.2': 'rgba(175, 82, 222, 0.2)',
      '0.25': 'rgba(175, 82, 222, 0.25)',
      '0.3': 'rgba(175, 82, 222, 0.3)',
      '0.35': 'rgba(175, 82, 222, 0.35)',
      '0.4': 'rgba(175, 82, 222, 0.4)',
      '0.6': 'rgba(175, 82, 222, 0.6)',
    },
  },

  // ── Backgrounds — Liquid Glass ──
  background: {
    light: {
      gradient:
        'linear-gradient(145deg, #F2F2F7 0%, #E5E5EA 30%, #D1D1D6 60%, #C7C7CC 100%)',
      paper: 'rgba(255, 255, 255, 0.72)',
      default: 'rgba(242, 242, 247, 0.95)',
    },
    dark: {
      gradient:
        'linear-gradient(145deg, #000000 0%, #0A0A14 25%, #111827 50%, #1C1C2E 75%, #1E293B 100%)',
      paper: 'rgba(28, 28, 46, 0.55)',
      default: 'rgba(0, 0, 0, 0.92)',
    },
  },

  // ── Text ──
  text: {
    light: {
      primary: 'rgba(0, 0, 0, 0.88)',
      secondary: 'rgba(60, 60, 67, 0.6)',
      disabled: 'rgba(60, 60, 67, 0.3)',
    },
    dark: {
      primary: 'rgba(255, 255, 255, 0.92)',
      secondary: 'rgba(235, 235, 245, 0.6)',
      disabled: 'rgba(235, 235, 245, 0.3)',
    },
  },

  // ── Borders — translucent for glass effect ──
  border: {
    light: {
      default: 'rgba(0, 0, 0, 0.12)',
      strong: 'rgba(0, 0, 0, 0.2)',
      weak: 'rgba(0, 0, 0, 0.06)',
    },
    dark: {
      default: 'rgba(255, 255, 255, 0.12)',
      strong: 'rgba(255, 255, 255, 0.2)',
      weak: 'rgba(255, 255, 255, 0.06)',
    },
  },

  // ── Shadows ──
  shadow: {
    light: {
      default: 'rgba(0, 0, 0, 0.08)',
      strong: 'rgba(0, 0, 0, 0.16)',
      weak: 'rgba(0, 0, 0, 0.04)',
    },
    dark: {
      default: 'rgba(0, 0, 0, 0.4)',
      strong: 'rgba(0, 0, 0, 0.6)',
      weak: 'rgba(0, 0, 0, 0.2)',
    },
  },
} as const;

/**
 * Helper to create gradient strings
 */
export const createGradient = (
  color1: string,
  color2: string,
  direction: string = '135deg',
): string => `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;

/**
 * Common gradient combinations
 */
export const gradients = {
  primary: createGradient(appColors.primary.main, appColors.secondary.main),
  primaryLight: createGradient(appColors.primary.light, appColors.secondary.light),
  primaryInfo: createGradient(appColors.primary.main, appColors.info.main),
  primaryInfoLight: createGradient(appColors.primary.light, appColors.info.light),
  ai: createGradient(appColors.ai.main, appColors.primary.main),
  aiLight: createGradient(appColors.ai.light, appColors.primary.light),
  success: createGradient(appColors.success.main, appColors.success.light),
  connectionActive: createGradient(appColors.success.main, appColors.success.light, '90deg'),
  connectionInactive: createGradient(appColors.primary.main, appColors.info.main, '90deg'),
} as const;
